import { useState, useRef, useEffect } from 'react';
import { api } from '../lib/api';

// Public "New Customer Account Form" — the online version of the paper intake
// form. Hosted at newcustomer.silverservice.com, no login. Mirrors the paper
// form section-by-section. Anti-spam is invisible to real customers: a honeypot
// field, a load-time timestamp (submits faster than a human are rejected), and
// an optional Cloudflare Turnstile widget (only shown when a site key is
// configured). Nothing here provisions an account — it emails our team, who
// review and set the customer up.

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const MAX_CERT_MB = 4;
const BUSINESS_TYPES = ['Restaurant', 'Hotel', 'Café', 'Office', 'Other'];

const EMPTY_FORM = {
  legalBusinessName: '',
  dbaName: '',
  businessType: '',
  businessTypeOther: '',
  billingAddress: '',
  shippingAddresses: [],
  mainContactName: '',
  mainContactEmail: '',
  mainContactPhone: '',
  apContactName: '',
  apContactEmail: '',
  businessPhone: '',
  ein: '',
  taxState: '',
  salesTaxLicense: '',
  paymentMethod: '',
  netDays: '',
  bankReference: '',
  tradeRef1: { company: '', contact: '', phone: '' },
  tradeRef2: { company: '', contact: '', phone: '' },
  authorizedSignerName: '',
  authorizedSignerTitle: '',
  acknowledgeName: '',
  acknowledgeTitle: '',
  acknowledgeAgreed: false,
};

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function NewCustomer() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [certFile, setCertFile] = useState(null);
  const [certError, setCertError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [showShipping, setShowShipping] = useState(false);
  const [gate, setGate] = useState('checking'); // 'checking' | 'ok' | 'denied'
  const [prospectName, setProspectName] = useState('');
  const inviteToken = useRef(typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('invite') || ''
    : '');
  const loadedAt = useRef(Date.now());

  // Invite gate — the form only opens with a valid invite token in the URL
  // (?invite=…). No/invalid token → a friendly "by invitation only" screen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!inviteToken.current) { setGate('denied'); return; }
      try {
        const res = await api.verifyNewCustomerInvite(inviteToken.current);
        if (cancelled) return;
        if (res && res.valid) { setGate('ok'); setProspectName(res.prospectName || ''); }
        else setGate('denied');
      } catch {
        if (!cancelled) setGate('denied');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setRef = (ref, k, v) => setForm(f => ({ ...f, [ref]: { ...f[ref], [k]: v } }));

  const addShipping = () => setForm(f => ({ ...f, shippingAddresses: [...f.shippingAddresses, ''] }));
  const setShipping = (i, v) => setForm(f => ({ ...f, shippingAddresses: f.shippingAddresses.map((a, idx) => idx === i ? v : a) }));
  const removeShipping = (i) => setForm(f => ({ ...f, shippingAddresses: f.shippingAddresses.filter((_, idx) => idx !== i) }));
  function toggleShipping() {
    setShowShipping(prev => {
      const next = !prev;
      if (next && form.shippingAddresses.length === 0) addShipping();
      return next;
    });
  }

  // Load the Cloudflare Turnstile widget only when a site key is configured, so
  // the form works fully without it (honeypot + timing still guard).
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    window.__ssTurnstileCb = (token) => setTurnstileToken(token || '');
    window.__ssTurnstileExpired = () => setTurnstileToken('');
    if (!document.getElementById('cf-turnstile-script')) {
      const s = document.createElement('script');
      s.id = 'cf-turnstile-script';
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
  }, []);

  async function handleCert(file) {
    setCertError('');
    if (!file) { setCertFile(null); return; }
    const okType = ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type);
    if (!okType) { setCertError('Please upload a PDF, JPG, or PNG.'); return; }
    if (file.size > MAX_CERT_MB * 1024 * 1024) { setCertError(`File is too large (max ${MAX_CERT_MB} MB).`); return; }
    setCertFile(file);
  }

  const canSubmit =
    form.legalBusinessName.trim() &&
    form.billingAddress.trim() &&
    form.mainContactName.trim() &&
    form.mainContactEmail.trim() &&
    form.mainContactPhone.trim() &&
    form.acknowledgeName.trim() &&
    form.acknowledgeAgreed &&
    (!TURNSTILE_SITE_KEY || turnstileToken);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      let resaleCertificate = '';
      if (certFile) resaleCertificate = await fileToDataUrl(certFile);
      await api.submitNewCustomer({
        ...form,
        shippingAddresses: form.shippingAddresses.map(a => a.trim()).filter(Boolean),
        resaleCertificate,
        inviteToken: inviteToken.current,
        website: honeypot,
        elapsedMs: Date.now() - loadedAt.current,
        turnstileToken,
      });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // Invite gate screens
  if (gate === 'checking') return (
    <div style={styles.page}>
      <Header />
      <div style={styles.successWrap}>
        <p style={{ ...styles.successText, marginTop: 0 }}>Checking your invitation…</p>
      </div>
    </div>
  );
  if (gate === 'denied') return (
    <div style={styles.page}>
      <Header />
      <div style={styles.successWrap}>
        <div style={{ ...styles.successIcon, borderColor: '#A50B18', color: '#A50B18', background: 'rgba(165,11,24,0.08)' }}>✉</div>
        <h2 style={styles.successTitle}>By invitation only</h2>
        <p style={styles.successText}>
          Our new-customer application is available by invitation. If you'd like to open a
          wholesale account with Silver Service, please email{' '}
          <a href="mailto:customerservice@silverservice.com" style={{ color: '#A50B18' }}>customerservice@silverservice.com</a>{' '}
          or call 303 840 4413 and we'll send you a link.
        </p>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={styles.page}>
      <Header />
      <div style={styles.successWrap}>
        <div style={styles.successIcon}>✓</div>
        <h2 style={styles.successTitle}>Application received</h2>
        <p style={styles.successText}>
          Thank you. Your new-customer application has been sent to our team.
          We'll review it and reach out shortly to finish setting up your account.
          Questions? Email <a href="mailto:customerservice@silverservice.com" style={{color:'#A50B18'}}>customerservice@silverservice.com</a>.
        </p>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      <Header />
      <div style={styles.intro}>
        <h1 style={styles.h1}>New Customer Account Form</h1>
        <p style={styles.introText}>
          {prospectName ? `Welcome, ${prospectName}! ` : 'Welcome to Silver Service Refreshment Systems. '}
          Fill out the form below to open a wholesale account. Fields marked <span style={styles.req}>*</span> are required.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* ── 1. Company information ── */}
        <Section n="1" title="Company Information" />
        <Field label="Legal business name" required>
          <input style={styles.input} value={form.legalBusinessName} onChange={e => set('legalBusinessName', e.target.value)} required />
        </Field>
        <Field label="DBA / trade name">
          <input style={styles.input} value={form.dbaName} onChange={e => set('dbaName', e.target.value)} />
        </Field>
        <Field label="Type of business">
          <div style={styles.radioRow}>
            {BUSINESS_TYPES.map(t => (
              <label key={t} style={styles.radioLabel}>
                <input type="radio" name="businessType" checked={form.businessType === t} onChange={() => set('businessType', t)} />
                {t}
              </label>
            ))}
          </div>
          {form.businessType === 'Other' && (
            <input style={{...styles.input, marginTop:8}} placeholder="Please describe" value={form.businessTypeOther} onChange={e => set('businessTypeOther', e.target.value)} />
          )}
        </Field>
        <Field label="Billing address" required>
          <textarea style={styles.textarea} value={form.billingAddress} onChange={e => set('billingAddress', e.target.value)} required />
        </Field>

        <div style={styles.field}>
          <button type="button" onClick={toggleShipping} style={styles.expandToggle}>
            <span style={styles.expandSign}>{showShipping ? '−' : '+'}</span>
            Ship to a different address? <span style={styles.optional}>(optional)</span>
          </button>
          {showShipping && (
            <div style={styles.shipBox}>
              <p style={styles.hint}>
                Add one or more shipping addresses if your orders go somewhere other than your billing address.
                Leave empty to ship to billing.
              </p>
              {form.shippingAddresses.map((addr, i) => (
                <div key={i} style={styles.shipRow}>
                  <textarea
                    style={styles.textarea}
                    placeholder={`Shipping address ${i + 1}`}
                    value={addr}
                    onChange={e => setShipping(i, e.target.value)}
                  />
                  <button type="button" onClick={() => removeShipping(i)} style={styles.removeBtn} aria-label="Remove address">✕</button>
                </div>
              ))}
              <button type="button" onClick={addShipping} style={styles.addBtn}>+ Add another shipping address</button>
            </div>
          )}
        </div>
        <Row>
          <Field label="Business phone">
            <input style={styles.input} type="tel" value={form.businessPhone} onChange={e => set('businessPhone', e.target.value)} />
          </Field>
          <Field label="EIN (optional)">
            <input style={styles.input} value={form.ein} onChange={e => set('ein', e.target.value)} />
          </Field>
        </Row>

        {/* ── Contacts ── */}
        <Row>
          <Field label="Main contact name" required>
            <input style={styles.input} value={form.mainContactName} onChange={e => set('mainContactName', e.target.value)} required />
          </Field>
          <Field label="Main contact phone" required>
            <input style={styles.input} type="tel" value={form.mainContactPhone} onChange={e => set('mainContactPhone', e.target.value)} required />
          </Field>
        </Row>
        <Field label="Main contact email" required>
          <input style={styles.input} type="email" value={form.mainContactEmail} onChange={e => set('mainContactEmail', e.target.value)} required />
        </Field>
        <Row>
          <Field label="Accounts-payable contact">
            <input style={styles.input} value={form.apContactName} onChange={e => set('apContactName', e.target.value)} />
          </Field>
          <Field label="Accounts-payable email">
            <input style={styles.input} type="email" value={form.apContactEmail} onChange={e => set('apContactEmail', e.target.value)} />
          </Field>
        </Row>

        {/* ── 2. Sales tax / resale ── */}
        <Section n="2" title="Sales Tax / Resale Certificate" />
        <Row>
          <Field label="State">
            <input style={styles.input} value={form.taxState} onChange={e => set('taxState', e.target.value)} />
          </Field>
          <Field label="Sales tax license #">
            <input style={styles.input} value={form.salesTaxLicense} onChange={e => set('salesTaxLicense', e.target.value)} />
          </Field>
        </Row>
        <Field label="Resale / tax-exemption certificate">
          <input type="file" accept=".pdf,image/jpeg,image/png" onChange={e => handleCert(e.target.files?.[0])} style={styles.file} />
          {certFile && <div style={styles.fileName}>Attached: {certFile.name}</div>}
          {certError && <div style={styles.inlineErr}>{certError}</div>}
          <p style={styles.hint}>
            Required for tax-exempt resale purchases — otherwise sales tax will be charged. PDF, JPG, or PNG up to {MAX_CERT_MB} MB.
          </p>
        </Field>

        {/* ── 3. Payment terms ── */}
        <Section n="3" title="Payment Terms" />
        <Field label="How would you like to pay?">
          <div style={styles.radioCol}>
            <label style={styles.radioLabel}>
              <input type="radio" name="paymentMethod" checked={form.paymentMethod === 'credit_card'} onChange={() => set('paymentMethod', 'credit_card')} />
              Credit card on file
            </label>
            <label style={styles.radioLabel}>
              <input type="radio" name="paymentMethod" checked={form.paymentMethod === 'cod'} onChange={() => set('paymentMethod', 'cod')} />
              COD / payment on delivery
            </label>
            <label style={styles.radioLabel}>
              <input type="radio" name="paymentMethod" checked={form.paymentMethod === 'net'} onChange={() => set('paymentMethod', 'net')} />
              Net terms (subject to credit approval)
            </label>
          </div>
        </Field>

        {form.paymentMethod === 'net' && (
          <div style={styles.netBox}>
            <Field label="Net — number of days">
              <input style={{...styles.input, maxWidth:140}} type="number" value={form.netDays} onChange={e => set('netDays', e.target.value)} placeholder="e.g. 30" />
            </Field>
            <Field label="Bank reference (name / branch / contact / phone)">
              <textarea style={styles.textarea} value={form.bankReference} onChange={e => set('bankReference', e.target.value)} />
            </Field>
            <div style={styles.subLabel}>Trade reference 1</div>
            <Row>
              <input style={styles.input} placeholder="Company" value={form.tradeRef1.company} onChange={e => setRef('tradeRef1', 'company', e.target.value)} />
              <input style={styles.input} placeholder="Contact" value={form.tradeRef1.contact} onChange={e => setRef('tradeRef1', 'contact', e.target.value)} />
              <input style={styles.input} placeholder="Phone" value={form.tradeRef1.phone} onChange={e => setRef('tradeRef1', 'phone', e.target.value)} />
            </Row>
            <div style={styles.subLabel}>Trade reference 2</div>
            <Row>
              <input style={styles.input} placeholder="Company" value={form.tradeRef2.company} onChange={e => setRef('tradeRef2', 'company', e.target.value)} />
              <input style={styles.input} placeholder="Contact" value={form.tradeRef2.contact} onChange={e => setRef('tradeRef2', 'contact', e.target.value)} />
              <input style={styles.input} placeholder="Phone" value={form.tradeRef2.phone} onChange={e => setRef('tradeRef2', 'phone', e.target.value)} />
            </Row>
          </div>
        )}

        <Row>
          <Field label="Authorized signer name">
            <input style={styles.input} value={form.authorizedSignerName} onChange={e => set('authorizedSignerName', e.target.value)} />
          </Field>
          <Field label="Title">
            <input style={styles.input} value={form.authorizedSignerTitle} onChange={e => set('authorizedSignerTitle', e.target.value)} />
          </Field>
        </Row>

        {/* ── 4. Acknowledgment ── */}
        <Section n="4" title="Acknowledgment" />
        <p style={styles.ackText}>
          By signing below, the customer certifies that the above information is true and
          correct and agrees to Silver Service Refreshment Systems' standard terms and
          conditions of sale. The customer authorizes Silver Service to obtain trade
          references if credit is requested.
        </p>
        <Row>
          <Field label="Full name (typed signature)" required>
            <input style={styles.input} value={form.acknowledgeName} onChange={e => set('acknowledgeName', e.target.value)} required />
          </Field>
          <Field label="Title">
            <input style={styles.input} value={form.acknowledgeTitle} onChange={e => set('acknowledgeTitle', e.target.value)} />
          </Field>
        </Row>
        <label style={styles.checkLabel}>
          <input type="checkbox" checked={form.acknowledgeAgreed} onChange={e => set('acknowledgeAgreed', e.target.checked)} />
          I certify the information above is true and correct. <span style={styles.req}>*</span>
        </label>

        {/* Honeypot — hidden from humans; bots fill it. */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={e => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={styles.honeypot}
        />

        {TURNSTILE_SITE_KEY && (
          <div
            className="cf-turnstile"
            data-sitekey={TURNSTILE_SITE_KEY}
            data-callback="__ssTurnstileCb"
            data-expired-callback="__ssTurnstileExpired"
            style={{ marginTop: 8 }}
          />
        )}

        {error && <div style={styles.error}>{error}</div>}

        <button type="submit" style={{...styles.btn, ...(canSubmit ? {} : styles.btnDisabled)}} disabled={!canSubmit || submitting}>
          {submitting ? 'Submitting…' : 'Submit application'}
        </button>
        <p style={styles.footerNote}>
          Prefer to talk to a person? Call 303 840 4413 or email customerservice@silverservice.com.
        </p>
      </form>
    </div>
  );
}

function Header() {
  return (
    <div style={styles.brandBar}>
      <img src="/logo-full.png" alt="Silver Service" style={styles.logoImg} />
    </div>
  );
}

function Section({ n, title }) {
  return (
    <div style={styles.section}>
      <span style={styles.sectionNum}>{n}</span>
      <h2 style={styles.sectionTitle}>{title}</h2>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}{required && <span style={styles.req}> *</span>}</label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={styles.row}>{children}</div>;
}

const styles = {
  page: { minHeight: '100vh', background: '#f5f5f5', paddingBottom: '3rem', fontFamily: "'Inter', sans-serif" },
  brandBar: { background: '#fff', padding: '1.5rem 1rem', textAlign: 'center', marginBottom: '2rem', borderBottom: '3px solid #A50B18' },
  logoImg: { height: 120, width: 'auto', display: 'inline-block' },
  intro: { maxWidth: 680, margin: '0 auto 1.5rem', padding: '0 1.25rem' },
  h1: { fontSize: '1.6rem', color: '#222', margin: '0 0 0.5rem', fontWeight: 700 },
  introText: { color: 'rgba(0,0,0,0.6)', fontSize: '0.92rem', lineHeight: 1.6, margin: 0 },
  req: { color: '#A50B18' },
  form: { maxWidth: 680, margin: '0 auto', background: '#fff', border: '1px solid #e4e0d9', borderRadius: 12, padding: '2rem 2rem 2.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  section: { display: 'flex', alignItems: 'center', gap: 12, margin: '1.25rem 0 0.25rem', paddingTop: '1rem', borderTop: '1px solid #eee' },
  sectionNum: { width: 26, height: 26, borderRadius: '50%', background: '#A50B18', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 },
  sectionTitle: { fontSize: '1.05rem', color: '#222', margin: 0, fontWeight: 700 },
  field: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontSize: '0.8rem', color: 'rgba(0,0,0,0.6)', fontWeight: 600 },
  input: { background: '#fff', border: '1px solid #d5d0c8', borderRadius: 6, padding: '0.65rem 0.8rem', color: '#222', fontSize: '0.92rem', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  textarea: { background: '#fff', border: '1px solid #d5d0c8', borderRadius: 6, padding: '0.65rem 0.8rem', color: '#222', fontSize: '0.92rem', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 68 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem' },
  radioRow: { display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.25rem' },
  radioCol: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', color: '#333', cursor: 'pointer' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', color: '#333', cursor: 'pointer' },
  subLabel: { fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)', fontWeight: 600, marginTop: 4 },
  expandToggle: { display: 'flex', alignItems: 'center', gap: 8, background: '#faf8f5', border: '1px solid #e4dfd7', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.9rem', color: '#333', fontWeight: 600, cursor: 'pointer', width: '100%', textAlign: 'left', fontFamily: 'inherit' },
  expandSign: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '50%', background: '#A50B18', color: '#fff', fontSize: '0.95rem', fontWeight: 700, flexShrink: 0 },
  optional: { color: 'rgba(0,0,0,0.4)', fontWeight: 400 },
  shipBox: { marginTop: 10, display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '1rem', background: '#faf8f5', border: '1px solid #ece7df', borderRadius: 8 },
  shipRow: { display: 'flex', gap: 8, alignItems: 'flex-start' },
  removeBtn: { background: 'none', border: '1px solid #ddd', color: '#999', borderRadius: 6, width: 34, height: 34, flexShrink: 0, cursor: 'pointer', fontSize: '0.85rem' },
  addBtn: { background: 'none', border: '1px dashed #A50B18', color: '#A50B18', borderRadius: 6, padding: '0.6rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  netBox: { background: '#faf8f5', border: '1px solid #ece7df', borderRadius: 8, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' },
  file: { fontSize: '0.85rem' },
  fileName: { fontSize: '0.82rem', color: '#2c7a3f', marginTop: 4 },
  inlineErr: { fontSize: '0.82rem', color: '#c0392b', marginTop: 4 },
  hint: { fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)', margin: '0.3rem 0 0', lineHeight: 1.5 },
  ackText: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.65)', lineHeight: 1.6, background: '#faf8f5', border: '1px solid #ece7df', borderRadius: 8, padding: '0.9rem 1rem', margin: 0 },
  honeypot: { position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0, pointerEvents: 'none' },
  error: { background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 6, padding: '0.75rem 1rem', color: '#c0392b', fontSize: '0.85rem' },
  btn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.9rem', fontSize: '0.98rem', fontWeight: 700, cursor: 'pointer', marginTop: '0.75rem' },
  btnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  footerNote: { fontSize: '0.8rem', color: 'rgba(0,0,0,0.5)', textAlign: 'center', margin: '0.4rem 0 0' },
  successWrap: { maxWidth: 460, margin: '2rem auto', textAlign: 'center', background: '#fff', border: '1px solid #e4e0d9', borderRadius: 12, padding: '2.5rem 2rem' },
  successIcon: { width: 64, height: 64, background: 'rgba(39,174,96,0.1)', border: '2px solid #27ae60', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '1.8rem', color: '#27ae60' },
  successTitle: { fontSize: '1.4rem', color: '#222', margin: '0 0 0.75rem' },
  successText: { color: 'rgba(0,0,0,0.6)', lineHeight: 1.65, margin: 0, fontSize: '0.92rem' },
};
